import { Award, GraduationCap, ScrollText, FileCheck2 } from "lucide-react";
import { Reveal, PageHero, SectionHead, Eyebrow, CTABand } from "@/components/Shared";
import CredentialsVault from "@/components/CredentialsVault";
import {
    founder,
    coreAreas,
    qualifications,
    professionalRecognition,
    disciplines,
    clientSegments,
} from "@/data/content";

export default function About() {
    return (
        <div data-testid="about-page">
            <PageHero
                index="01"
                overline="About"
                title="Every investigation begins with a question."
                lead="Our role is to find the answer through facts, data and evidence. Infrasic Intelligence works at the intersection of finance, investigation, law and technology. We investigate suspected fraud and misconduct, assess whistleblower allegations, analyse complex financial information and bank transactions, examine digital evidence, trace funds and assets, conduct investigative due diligence and help organisations identify and understand fraud-related risks. Our work is designed for matters where facts need to be established, information needs to be connected and evidence needs to be understood."
                testId="about-hero"
            />

            {/* OUR PERSPECTIVE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="perspective-section">
                <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
                    <SectionHead
                        index="02"
                        overline="Our Perspective"
                        title="Financial records. Digital evidence. Corporate information."
                        testId="perspective"
                    />
                    <Reveal delay={0.1}>
                        <div className="certificate h-full p-10 lg:p-12">
                            <div className="space-y-5 font-display text-lg font-medium leading-relaxed text-[#E8ECF4] sm:text-xl">
                                <p data-testid="perspective-line-0">Financial records may explain <span className="italic text-[#5B93E8]">what</span> happened.</p>
                                <p data-testid="perspective-line-1">Digital evidence may explain <span className="italic text-[#5B93E8]">how</span> it happened.</p>
                                <p data-testid="perspective-line-2">Corporate information may identify <span className="italic text-[#5B93E8]">who</span> was connected.</p>
                                <p data-testid="perspective-line-3">Communications may provide <span className="italic text-[#5B93E8]">context</span> relevant to relationships, actions or intent.</p>
                            </div>
                            <div className="mx-auto mt-10 h-px w-16 bg-[#A87B2F]" />
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* LEADERSHIP */}
            <section className="border-y border-[#243650] bg-[#0F1B2E]" data-testid="leadership-section">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                    <SectionHead
                        index="03"
                        overline="Leadership"
                        title="Experience behind the investigation."
                        testId="leadership"
                    />
                    <div className="mt-14 grid gap-8 lg:grid-cols-[0.9fr_1.1fr]">
                        <Reveal>
                            <div className="certificate h-full p-10 text-center lg:p-12" data-testid="founder-card">
                                <img
                                    src={founder.image}
                                    alt={`${founder.name} portrait`}
                                    className="mx-auto h-64 w-full max-w-[420px] border border-[#D9A84E]/80 object-cover object-top bg-[#F2EEE4] shadow-[0_0_0_6px_rgba(217,168,78,0.12)]"
                                />
                                <h3 className="mt-8 font-display text-3xl font-semibold uppercase tracking-wide text-[#E8ECF4]" data-testid="founder-name">
                                    {founder.name}
                                </h3>
                                <p className="mt-2 font-tech text-[11px] font-semibold uppercase tracking-[0.26em] text-[#5B93E8]" data-testid="founder-role">
                                    {founder.role}
                                </p>
                                <div className="mx-auto mt-5 h-px w-14 bg-[#A87B2F]" />
                                <p className="mt-5 text-sm font-medium text-[#E8ECF4]" data-testid="founder-credentials">
                                    {founder.credentials}
                                </p>
                                <p className="mt-3 font-display text-lg font-semibold text-[#D9A84E]" data-testid="founder-experience">
                                    {founder.experience}
                                </p>
                                <p className="mt-4 font-tech text-[10px] uppercase tracking-[0.2em] text-[#8C97AC]">
                                    {founder.tags}
                                </p>
                                <a
                                    href="#credentials-vault"
                                    data-testid="founder-view-certificate"
                                    className="group mt-7 inline-flex items-center gap-2.5 border border-[#5B93E8]/35 bg-[#16263F] px-5 py-3 font-tech text-[10px] font-semibold uppercase tracking-[0.2em] text-[#5B93E8] transition-colors duration-300 hover:border-[#5B93E8] hover:bg-[#3D74C6] hover:text-white"
                                >
                                    <FileCheck2 className="h-4 w-4" />
                                    View CFE Certificate
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.12}>
                            <div className="flex h-full flex-col border border-[#243650] bg-[#0B1322] p-10 lg:p-12">
                                <p className="text-base leading-relaxed text-[#9AA7BF]" data-testid="founder-bio">
                                    {founder.bio}
                                </p>
                                <ul className="mt-8 space-y-5">
                                    {founder.highlights.map((h, i) => (
                                        <li key={i} className="flex gap-4" data-testid={`founder-highlight-${i}`}>
                                            <span className="mt-2 h-2 w-2 shrink-0 bg-[#A87B2F]" />
                                            <span className="text-sm leading-relaxed text-[#9AA7BF]">{h}</span>
                                        </li>
                                    ))}
                                </ul>
                                <p className="mt-8 border-t border-[#243650] pt-6 text-xs italic leading-relaxed text-[#8C97AC]" data-testid="founder-disclaimer">
                                    {founder.disclaimer}
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* CORE AREAS / QUALIFICATIONS / RECOGNITION */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="credentials-section">
                <div className="grid gap-8 lg:grid-cols-3">
                    <Reveal>
                        <div className="card-lift h-full border border-[#243650] bg-[#0F1B2E] p-9" data-testid="core-areas-card">
                            <ScrollText className="h-6 w-6 text-[#D9A84E]" />
                            <Eyebrow label="Core Areas" />
                            <div className="mt-6 flex flex-wrap gap-2.5">
                                {coreAreas.map((area, i) => (
                                    <span
                                        key={area}
                                        data-testid={`core-area-${i}`}
                                        className="border border-white/10 bg-[#0B1322] px-3.5 py-2 font-tech text-[10px] uppercase tracking-[0.14em] text-[#E8ECF4]"
                                    >
                                        {area}
                                    </span>
                                ))}
                            </div>
                        </div>
                    </Reveal>
                    <Reveal delay={0.08}>
                        <div className="card-lift h-full border border-[#243650] bg-[#0F1B2E] p-9" data-testid="qualifications-card">
                            <GraduationCap className="h-6 w-6 text-[#D9A84E]" />
                            <Eyebrow label="Qualifications" />
                            <ul className="mt-6 space-y-5">
                                {qualifications.map((q, i) => (
                                    <li key={i} className="border-l-[3px] border-[#5B93E8]/40 pl-4" data-testid={`qualification-${i}`}>
                                        <p className="font-display text-base font-semibold text-[#E8ECF4]">{q.title}</p>
                                        {q.organisation && (
                                            <p className="mt-1 text-xs leading-relaxed text-[#8C97AC]">{q.organisation}</p>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </Reveal>
                    <Reveal delay={0.16}>
                        <div className="card-lift h-full border border-[#243650] bg-[#0F1B2E] p-9" data-testid="recognition-card">
                            <Award className="h-6 w-6 text-[#D9A84E]" />
                            <Eyebrow label="Professional Recognition" />
                            <p className="mt-6 text-sm leading-relaxed text-[#9AA7BF]">
                                {professionalRecognition}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* MULTIDISCIPLINARY CAPABILITY */}
            <section className="border-y border-[#243650] bg-[#0F1B2E]" data-testid="disciplines-section">
                <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28">
                    <SectionHead
                        index="04"
                        overline="Multidisciplinary Capability"
                        title="Different disciplines. One investigative approach."
                        testId="disciplines"
                    />
                    <div className="mt-14 grid gap-px border border-[#243650] bg-[#243650] sm:grid-cols-2 lg:grid-cols-3">
                        {disciplines.map((d, i) => (
                            <Reveal key={d.title} delay={(i % 3) * 0.06}>
                                <div
                                    className="group h-full bg-[#0F1B2E] p-8 transition-colors duration-300 hover:bg-[#163050]"
                                    data-testid={`discipline-${i}`}
                                >
                                    <span className="inline-flex border border-[#243650] p-3 transition-colors duration-300 group-hover:border-white/20">
                                        <d.icon className="h-5 w-5 text-[#5B93E8] transition-colors duration-300 group-hover:text-[#D9B36A]" />
                                    </span>
                                    <h3 className="mt-5 font-display text-lg font-semibold text-[#E8ECF4] transition-colors duration-300 group-hover:text-white">
                                        {d.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-[#9AA7BF] transition-colors duration-300 group-hover:text-white/70">
                                        {d.description}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHO WE SERVE */}
            <section className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-28" data-testid="clients-section">
                <SectionHead
                    index="05"
                    overline="Who We Serve"
                    title="Where facts, evidence and risk matter."
                    testId="clients"
                />
                <div className="mt-14 border border-[#243650]">
                    {clientSegments.map((c, i) => (
                        <Reveal key={c.title} delay={0.03}>
                            <div
                                className={`group grid items-center gap-5 bg-[#0F1B2E] px-7 py-7 transition-colors duration-300 hover:bg-[#0B1322] sm:grid-cols-[64px_240px_1fr] lg:px-10 ${
                                    i !== 0 ? "border-t border-[#243650]" : ""
                                }`}
                                data-testid={`client-segment-${i}`}
                            >
                                <span className="flex items-center gap-4">
                                    <span className="font-tech text-[10px] tracking-[0.18em] text-[#D9A84E]">
                                        {String(i + 1).padStart(2, "0")}
                                    </span>
                                    <c.icon className="h-5 w-5 text-[#5B93E8]" />
                                </span>
                                <h3 className="font-display text-lg font-semibold text-[#E8ECF4]">
                                    {c.title}
                                </h3>
                                <p className="text-sm leading-relaxed text-[#9AA7BF]">
                                    {c.description}
                                </p>
                            </div>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* RECOGNITIONS & CREDENTIALS — Credentials Vault */}
            <CredentialsVault />

            <CTABand testId="about-cta" />
        </div>
    );
}
